import { getFragmentData, graphql } from '@e-socna/data-layer';
import { EMPTY_OBJECT } from '@e-socna/empty';
import type { FragmentType } from '@e-socna/data-layer';

import { Tabs, TabsStrategy, TabsVariant } from '../../components/Tabs';
import { Participations } from '../../components/Participations';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';

import { Admin } from './Admin';
import { Memberships } from './Memberships';
import { GeneralInfo } from './GeneralInfo';
import { Versions } from './Versions';
import { ContactInfo } from './ContactInfo';
import { Addresses } from './Addresses';
import { Billing } from './Billing';
import { BankInfo } from './BankInfo';
import { Pennylane } from './Pennylane';

export const PersonPage_Tabs_Person_Fragment = graphql(`
  fragment PersonPage_Tabs_Person_Fragment on Person {
    _id
    ...PersonPage_GeneralInfo_Person_Fragment
    ...PersonPage_Memberships_Person_Fragment
    ...PersonPage_Addresses_Person_Fragment
    ...PersonPage_ContactInfo_Person_Fragment
    ...PersonPage_BankInfo_Person_Fragment
    ...PersonPage_Billing_Person_Fragment
    ...PersonPage_Admin_Person_Fragment
    ...PersonPage_Pennylane_Person_Fragment
  }
`);

export type PersonPaths = Record<
  | 'addresses'
  | 'addressesEdit'
  | 'addressesEditDelete'
  | 'addressesEditModify'
  | 'addressesEditNew'
  | 'admin'
  | 'adminEdit'
  | 'bankInfo'
  | 'bankInfoEdit'
  | 'billing'
  | 'billingEdit'
  | 'contactInfo'
  | 'contactInfoEdit'
  | 'default'
  | 'generalInfo'
  | 'generalInfoEdit'
  | 'memberships'
  | 'membershipsEdit'
  | 'participations'
  | 'pennylane'
  | 'versions',
  string
>;

interface Props {
  readonly paths: PersonPaths;
  readonly person: FragmentType<typeof PersonPage_Tabs_Person_Fragment>;
  readonly variant?: TabsVariant;
}

export const PersonTabs = ({
  paths,
  person: pPerson,
  variant,
}: Readonly<Props>) => {
  const person = getFragmentData(PersonPage_Tabs_Person_Fragment, pPerson);

  const { getActiveTab } = useBuildPaths(paths, EMPTY_OBJECT, {
    addresses: 'addresses',
    addressesEdit: 'addresses',
    addressesEditDelete: 'addresses',
    addressesEditModify: 'addresses',
    addressesEditNew: 'addresses',
    admin: 'admin',
    adminEdit: 'admin',
    bankInfo: 'bankInfo',
    bankInfoEdit: 'bankInfo',
    billing: 'billing',
    billingEdit: 'billing',
    contactInfo: 'contactInfo',
    contactInfoEdit: 'contactInfo',
    default: 'generalInfo',
    generalInfo: 'generalInfo',
    generalInfoEdit: 'generalInfo',
    memberships: 'memberships',
    membershipsEdit: 'memberships',
    participations: 'participations',
    pennylane: 'pennylane',
    versions: 'versions',
  });

  const generalInfoPaths = {
    consult: paths.generalInfo,
    edit: paths.generalInfoEdit,
  } as const;

  const contactInfoPaths = {
    consult: paths.contactInfo,
    edit: paths.contactInfoEdit,
  } as const;

  const billingPaths = {
    consult: paths.billing,
    edit: paths.billingEdit,
  } as const;

  const bankInfoPaths = {
    consult: paths.bankInfo,
    edit: paths.bankInfoEdit,
  } as const;

  const membershipsPaths = {
    consult: paths.memberships,
    edit: paths.membershipsEdit,
  } as const;

  const adminPaths = {
    consult: paths.admin,
    edit: paths.adminEdit,
  } as const;

  const addressPaths =
    paths.addresses &&
    paths.addressesEdit &&
    paths.addressesEditNew &&
    paths.addressesEditModify &&
    paths.addressesEditDelete
      ? ({
          consult: paths.addresses,
          edit: paths.addressesEdit,
          editNew: paths.addressesEditNew,
          editModify: paths.addressesEditModify,
          editDelete: paths.addressesEditDelete,
        } as const)
      : undefined;

  return (
    <Tabs
      activeTab={getActiveTab()}
      id="person-tabs"
      strategy={TabsStrategy.Controlled}
      tabs={[
        {
          key: 'generalInfo',
          label: 'Informations Générales',
          panel: <GeneralInfo paths={generalInfoPaths} person={person} />,
          to: paths.generalInfo,
          value: 'generalInfo',
        },
        {
          key: 'contactInfo',
          label: 'Contact',
          panel: <ContactInfo paths={contactInfoPaths} person={person} />,
          to: paths.contactInfo,
          value: 'contactInfo',
        },
        {
          key: 'addresses',
          label: 'Adresses',
          panel: <Addresses paths={addressPaths} person={person} />,
          to: paths.addresses,
          value: 'addresses',
        },
        {
          key: 'billing',
          label: 'Facturation',
          panel: <Billing paths={billingPaths} person={person} />,
          to: paths.billing,
          value: 'billing',
        },
        {
          key: 'bankInfo',
          label: 'Bancaire',
          panel: <BankInfo paths={bankInfoPaths} person={person} />,
          to: paths.bankInfo,
          value: 'bankInfo',
        },
        {
          key: 'memberships',
          label: 'Organisations',
          panel: <Memberships member={person} paths={membershipsPaths} />,
          to: paths.memberships,
          value: 'memberships',
        },
        {
          key: 'participations',
          label: 'Missions Liées',
          panel: <Participations thirdPartyId={person._id} />,
          to: paths.participations,
          value: 'participations',
        },
        {
          key: 'pennylane',
          label: 'Pennylane',
          panel: <Pennylane person={person} />,
          to: paths.pennylane,
          value: 'pennylane',
        },
        {
          key: 'versions',
          label: 'Versions',
          panel: <Versions originalId={person._id} />,
          to: paths.versions,
          value: 'versions',
        },
        {
          key: 'admin',
          label: 'Administration',
          panel: <Admin paths={adminPaths} person={person} />,
          to: paths.admin,
          value: 'admin',
        },
      ].filter(
        (tab) => Object.keys(paths).findIndex((key) => key === tab.key) > -1,
      )}
      title="Onglets de l'écran de Fiche d'une Personne"
      variant={variant ?? TabsVariant.Horizontal}
    />
  );
};
